import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { billingApi, billingQueryKeys } from "../../shared/api/billing";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import {
  formatDate,
  formatDateTime,
  formatVndWithUnit,
  isoDate,
  paymentMethodLabel,
} from "../../shared/format";
import type {
  Payment,
  PaymentListFilter,
  PaymentMethod,
} from "../../shared/types/billing";
import { PageHeader } from "../../shared/ui/PageHeader";
import { DateRangePicker } from "../../shared/ui/DateRangePicker";
import { ErrorState } from "../../shared/ui/ErrorState";
import { PatientLink } from "../../shared/ui/PatientLink";
import { Toolbar } from "../../shared/ui/Toolbar";
import { DataTable, type Column } from "../../shared/ui/DataTable";
import { Pagination } from "../../shared/ui/Pagination";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 50;

const METHOD_OPTIONS: { value: PaymentMethod | ""; label: string }[] = [
  { value: "", label: "Tất cả phương thức" },
  { value: "CASH", label: paymentMethodLabel.CASH },
  { value: "CARD", label: paymentMethodLabel.CARD },
  { value: "BANK_TRANSFER", label: paymentMethodLabel.BANK_TRANSFER },
  { value: "VNPAY", label: paymentMethodLabel.VNPAY },
  { value: "MOMO", label: paymentMethodLabel.MOMO },
];

const METHOD_ALL = "__ALL__";

export function PaymentListPage() {
  const navigate = useNavigate();
  // Bộ lọc nằm trên URL; mặc định hôm nay (không ghi lên URL khi chưa đổi).
  const { filters, set } = useUrlFilters({
    q: param.string(),
    method: param.oneOf(METHOD_OPTIONS.map((o) => o.value).filter(Boolean) as PaymentMethod[]),
    from: param.isoDate(() => isoDate(new Date())),
    to: param.isoDate(() => isoDate(new Date())),
    page: param.int(),
  });
  const { q, method, from: fromDate, to: toDate, page } = filters;
  const debouncedQ = useDebounce(q, 250);

  const filter: PaymentListFilter = useMemo(
    () => ({
      q: debouncedQ || null,
      method: method || null,
      fromDate: fromDate || null,
      toDate: toDate || null,
    }),
    [debouncedQ, method, fromDate, toDate]
  );

  const list = useQuery({
    queryKey: billingQueryKeys.paymentList(filter, page),
    queryFn: () => billingApi.listPayments(filter, { page, size: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const totals = useQuery({
    queryKey: billingQueryKeys.paymentTotals(filter),
    queryFn: () => billingApi.paymentTotalsByMethod(filter),
  });

  const totalsByMethod = useMemo(() => {
    const map = new Map<PaymentMethod, { count: number; amount: number }>();
    for (const t of totals.data ?? []) {
      map.set(t.method, { count: t.count, amount: t.amount });
    }
    return map;
  }, [totals.data]);

  const grandTotal = (totals.data ?? []).reduce((s, t) => s + t.amount, 0);
  const grandCount = (totals.data ?? []).reduce((s, t) => s + t.count, 0);
  // Đang tải / lỗi thì KHÔNG hiện 0 ₫ (#19845).
  const kpi = (v: string) => (totals.isLoading ? "…" : totals.isError ? "—" : v);

  const columns: Column<Payment>[] = [
    {
      key: "when",
      header: "Thời gian",
      width: 160,
      render: (p) => (
        <div>
          <div className="font-semibold">{formatDateTime(p.receivedAt)}</div>
        </div>
      ),
    },
    {
      key: "invoice",
      header: "Hoá đơn",
      width: 170,
      render: (p) => (
        <Link
          to={`/invoices/${p.invoiceId}`}
          className="font-mono text-primary hover:underline"
        >
          {p.invoiceCode}
        </Link>
      ),
    },
    {
      key: "patient",
      header: "Bệnh nhân",
      render: (p) => <PatientLink id={p.patientId} name={p.patientName} />,
    },
    {
      key: "method",
      header: "Phương thức",
      width: 150,
      render: (p) => (
        <Badge variant="secondary">{paymentMethodLabel[p.method]}</Badge>
      ),
    },
    {
      key: "ref",
      header: "Tham chiếu",
      width: 180,
      render: (p) => (
        <span className="font-mono">{p.reference ?? "—"}</span>
      ),
    },
    {
      key: "amount",
      header: "Số tiền",
      width: 150,
      numeric: true,
      render: (p) => (
        <strong className="font-semibold">{formatVndWithUnit(p.amount)}</strong>
      ),
    },
    {
      key: "cashier",
      header: "Thu ngân",
      width: 160,
      render: (p) => p.cashierName || "—",
    },
  ];

  const data = list.data?.content ?? [];

  return (
    <div>
      <PageHeader
        title="Sổ thanh toán"
        description="Tất cả các khoản đã thu trong kỳ."
        actions={
          <Button asChild variant="link">
            <Link to="/invoices">
              <ArrowLeft className="h-4 w-4" />
              Hoá đơn
            </Link>
          </Button>
        }
      />

      {totals.isError && (
        <div className="mb-3">
          <ErrorState
            inline
            error={totals.error}
            title="Không tải được số tổng theo bộ lọc"
            onRetry={() => totals.refetch()}
          />
        </div>
      )}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiTile
          tone="primary"
          label="Tổng thu (theo bộ lọc)"
          value={kpi(formatVndWithUnit(grandTotal))}
          sublabel={kpi(`${grandCount} giao dịch`)}
        />
        {METHOD_OPTIONS.slice(1).map((opt) => {
          const t = totalsByMethod.get(opt.value as PaymentMethod);
          return (
            <KpiTile
              key={opt.value}
              tone="neutral"
              label={opt.label}
              value={kpi(formatVndWithUnit(t?.amount ?? 0))}
              sublabel={kpi(`${t?.count ?? 0} giao dịch`)}
            />
          );
        })}
      </div>

      <Toolbar
        search={{
          value: q,
          onChange: (v) => set({ q: v }),
          placeholder: "Tìm theo số HĐ, tên bệnh nhân, tham chiếu…",
        }}
        filters={
          <>
            <Select
              value={method === "" ? METHOD_ALL : method}
              onValueChange={(v) =>
                set({ method: v === METHOD_ALL ? "" : (v as PaymentMethod) })
              }
            >
              <SelectTrigger
                className="w-[190px]"
                aria-label="Phương thức"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METHOD_OPTIONS.map((o) => (
                  <SelectItem
                    key={o.value}
                    value={o.value === "" ? METHOD_ALL : o.value}
                  >
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangePicker
              className="w-[260px]"
              ariaLabel="Khoảng ngày thu chi"
              from={fromDate}
              to={toDate}
              onChange={({ from, to }) => set({ from, to })}
            />
          </>
        }
      />

      <DataTable
        ariaLabel="Sổ thanh toán"
        columns={columns}
        data={data}
        loading={list.isLoading}
        error={list.error}
        rowKey={(p) => p.id}
        onRowClick={(p) => navigate(`/invoices/${p.invoiceId}`)}
        empty={{
          title: "Chưa có thanh toán",
          description: `Không có giao dịch nào trong khoảng ${formatDate(fromDate)} – ${formatDate(toDate)}.`,
        }}
        footer={
          list.data && list.data.totalElements > PAGE_SIZE ? (
            <Pagination
              page={list.data.number}
              totalPages={list.data.totalPages}
              totalElements={list.data.totalElements}
              pageSize={list.data.size}
              onChange={(p) => set({ page: p })}
            />
          ) : null
        }
      />
    </div>
  );
}

function KpiTile({
  tone,
  label,
  value,
  sublabel,
}: {
  tone: "primary" | "success" | "warning" | "danger" | "neutral";
  label: string;
  value: string;
  sublabel?: string;
}) {
  return (
    <Card className={tone === "primary" ? "border-primary/40 bg-primary/5" : undefined}>
      <CardContent className="p-4">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div
          className={`mt-1 text-xl font-semibold tabular-nums ${
            tone === "primary" ? "text-primary" : "text-foreground"
          }`}
        >
          {value}
        </div>
        {sublabel && (
          <div className="mt-0.5 text-xs text-muted-foreground">{sublabel}</div>
        )}
      </CardContent>
    </Card>
  );
}
